import React, { useState } from 'react'
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, MenuItem, TextField } from '@mui/material'
import { useFormikContext } from 'formik'
import CustomMuiThemeProvider from 'src/designSystem/CustomMuiThemeProvider'
import { ClosableDialogTitle } from 'src/legacy/components/mui'
import { FollowUpProgram } from 'src/graphql.generated'
import { CoachProfileWithCoachID } from '../../SetCareTeamForm/useLoadCoachProfiles'
import { CareTeam } from '../../SetCareTeamForm/useLoadSetCareTeamData'
import SelectCareTeamToKeep from './SelectCareTeamToKeep'

type Props = {
  followUpProgram: FollowUpProgram
  oldCareTeam?: CareTeam
  coachProfiles: Array<CoachProfileWithCoachID>
  onTransition: () => void
  children: (open: () => void) => React.ReactNode
}

const TerminateAdvancedPatientDialog = ({
  followUpProgram,
  oldCareTeam,
  coachProfiles,
  onTransition,
  children,
}: Props) => {
  const { setFieldValue, submitForm } = useFormikContext<{ followUpProgramID?: string }>()
  const [open, setOpen] = useState(false)
  const recommendedProgramID = followUpProgram.programID
  const [selectedProgramID, setSelectedProgramID] = useState(recommendedProgramID ?? '')

  const close = () => setOpen(false)

  const transition = async () => {
    // Setting followUpProgramID unmounts this dialog (it flips the parent's "no follow-up selected" gate), so
    // we must set the value imperatively here rather than from an effect. setFieldValue is typed as returning
    // void but resolves a promise at runtime; awaiting it commits the value before we move on to careteam review.
    // eslint-disable-next-line @typescript-eslint/await-thenable
    await setFieldValue('followUpProgramID', selectedProgramID)
    close()
    onTransition()
  }

  const endCare = async () => {
    close()
    await submitForm()
  }

  return (
    <>
      {children(() => setOpen(true))}
      <CustomMuiThemeProvider>
        <Dialog fullWidth maxWidth="xs" onClose={close} open={open}>
          <ClosableDialogTitle onClose={close}>End care for an advanced patient?</ClosableDialogTitle>
          <DialogContent>
            <DialogContentText color="textPrimary" gutterBottom>
              This is an advanced patient. In most cases they should be transitioned to a follow-up program rather than
              having their care ended.
            </DialogContentText>
            <DialogContentText color="textPrimary">
              You can configure details below and transition the patient to a new program or proceed with ending the
              care on the bottom.
            </DialogContentText>
            {recommendedProgramID && (
              <TextField
                select
                fullWidth
                label="Follow-up program"
                value={selectedProgramID}
                onChange={(event) => setSelectedProgramID(event.target.value)}
                sx={{ mt: 2 }}
              >
                {followUpProgram.programOptions.map((programID) => (
                  <MenuItem key={programID} value={programID}>
                    {programID}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <SelectCareTeamToKeep
              followUpProgramID={selectedProgramID}
              oldCareTeam={oldCareTeam}
              coachProfiles={coachProfiles}
            />
          </DialogContent>
          <DialogActions>
            <Button color="inherit" onClick={close}>
              Cancel
            </Button>
            {recommendedProgramID ? (
              <>
                <Button color="inherit" onClick={endCare}>
                  End care anyway
                </Button>
                <Button color="primary" disabled={!selectedProgramID} onClick={transition}>
                  Transition
                </Button>
              </>
            ) : (
              <Button color="primary" onClick={endCare}>
                End care
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </CustomMuiThemeProvider>
    </>
  )
}

export default TerminateAdvancedPatientDialog
